/**
 * WT1 - the real network behind the walk-through queue (lib/tourQueue.ts drains through this).
 *
 * The API calls go through `api` (so they carry the session, renew it on a 401, and turn a lost connection
 * into NetworkError). The photo BYTES do not: they are PUT straight to the signed url the server minted,
 * which carries its own token - no Authorization header, and nothing passes through Railway.
 */

import { api, APIError, NetworkError } from './api';
import type { TourTransport } from './tourQueue';

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

/** Storage's own words for a refusal, when it gave any (it answers JSON: `{ statusCode, error, message }`). */
const readStorageRefusal = async (res: Response): Promise<{ detail: string | null; body: unknown }> => {
  let text = '';
  try {
    text = await res.text();
  } catch {
    return { detail: null, body: undefined };
  }
  try {
    const body: unknown = JSON.parse(text);
    const detail = isRecord(body)
      ? [body.message, body.error].find((v): v is string => typeof v === 'string' && v.trim() !== '') ?? null
      : null;
    return { detail, body };
  } catch {
    return { detail: text.trim() ? text.trim().slice(0, 200) : null, body: undefined };
  }
};

/**
 * PUT the bytes to a signed upload url. On a refusal it throws an APIError carrying the HTTP STATUS - the
 * queue reads it to tell an expired url (400/401/403: get a fresh one) from anything else. A fetch that
 * never reached storage throws NetworkError, which the queue reads as "waiting for signal".
 */
export const putBytesToSignedUrl = async (url: string, bytes: ArrayBuffer, contentType: string): Promise<void> => {
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: bytes,
    });
  } catch (err) {
    if (err instanceof TypeError) throw new NetworkError();
    throw err;
  }
  if (res.ok) return;
  const { detail, body } = await readStorageRefusal(res);
  throw new APIError(
    res.status,
    `Storage refused the photo (HTTP ${res.status})${detail ? `: ${detail}` : ''}`,
    body,
  );
};

export const tourTransport: TourTransport = {
  requestUploadUrl: async (tourId, photoId, { capturedAt, contentType }) => {
    const res = await api.tourPhotoUploadUrl(tourId, photoId, { captured_at: capturedAt, content_type: contentType });
    // Refuse rather than guess: PUTting to "undefined" would read as a storage refusal, not the real cause.
    if (typeof res?.upload_url !== 'string' || res.upload_url === '') {
      throw new Error('The server did not return an upload address for this photo.');
    }
    return res.upload_url;
  },

  putBytes: putBytesToSignedUrl,

  savePhoto: async (tourId, photoId, body) => {
    await api.saveTourPhoto(tourId, photoId, body);
  },

  patchPhoto: async (tourId, photoId, body) => {
    await api.patchTourPhoto(tourId, photoId, body);
  },

  patchTour: async (tourId, body) => {
    await api.patchTour(tourId, body);
  },
};
